import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

/**
 * Custom hook for MKT Cost by Projects auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration
 * - Polling for data changes every 500ms
 * - Manual update flag to prevent polling overwrites
 * - Pending deletes tracking
 * 
 */
export const useMKTCostAutoSave = (
    isMKTEditing: boolean,
    mktCostProjectsData: any[],
    MKTRef: React.RefObject<any>,
    setMktCostProjectsData: (data: any) => void,
    expandAllCategoriesWithChildren?: (data: any[]) => void
) => {
    // Auto-save state
    const [MKTAutoSaveVisible, setMKTAutoSaveVisible] = useState(false);
    const [MKTEditData, setMKTEditData] = useState<any[]>([]);
    const [MKTPendingDeletes, setMKTPendingDeletes] = useState<any[]>([]);
    const isMKTManualUpdateRef = useRef(false);

    // Auto-save: construct unique pageKey for MKT Cost by Projects
    const MKTPageKey = useMemo(() => {
        return EDIT_PAGE_KEY_MAP.publishing_mktcost_mkt_cost;
    }, []);

    // Auto-save: get methods (check if auto-save exists) - First call without dependencies
    const MKTAutoSaveMethods: any = useAutoSaveParams({
        pageKey: MKTPageKey,
    });

    const {
        getSaveParamsValue: getMKTSaveParamsValue,
        showRestorePrompt: showMKTRestorePrompt,
        deleteSaveParamsByPageKey: deleteMKTSaveParamsByPageKey
    } = MKTAutoSaveMethods;

    // Track edit data changes for MKT Cost auto-save - Poll frequently to capture real-time edits
    useEffect(() => {
        if (isMKTEditing && MKTRef.current) {
            // Initialize edit data when entering edit mode
            if (mktCostProjectsData.length > 0) {
                setMKTEditData(mktCostProjectsData);
            }

            const interval = setInterval(() => {
                // Skip polling if we just did a manual update (like deletion/reorder)
                if (isMKTManualUpdateRef.current) {
                    isMKTManualUpdateRef.current = false;
                    return;
                }

                const currentEditData = MKTRef.current?.getEditData?.();
                if (currentEditData && Array.isArray(currentEditData)) {
                    setMKTEditData((prev: any[]) => {
                        const prevStr = JSON.stringify(prev);
                        const currentStr = JSON.stringify(currentEditData);
                        if (prevStr !== currentStr) {
                            return currentEditData;
                        }
                        return prev;
                    });
                }
            }, 500); // Check every 500ms for changes

            return () => clearInterval(interval);
        } else if (!isMKTEditing) {
            // Reset when exiting edit mode
            setMKTEditData([]);
            MKTInitialParamsRef.current = {
                tableData: [],
                pendingDeletes: [],
            };
        }
    }, [isMKTEditing, mktCostProjectsData, MKTRef]);

    // 🔥 CRITICAL: Only compute MKT currentSaveParams when in edit mode to prevent race conditions
    // Only save actual data changes (tableData and pendingDeletes), NOT UI state like expandedKeys
    const MKTCurrentSaveParams = useMemo(() => {
        if (!isMKTEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                tableData: [],
                pendingDeletes: [],
            };
        }

        // When editing, use actual edit data
        return {
            tableData: MKTEditData.length > 0 ? MKTEditData : mktCostProjectsData,
            pendingDeletes: MKTPendingDeletes,
        };
    }, [isMKTEditing, MKTEditData, mktCostProjectsData, MKTPendingDeletes]);

    // 🔥 CRITICAL: Track initial params to detect real changes
    const MKTInitialParamsRef = useRef({
        tableData: [],
        pendingDeletes: [],
    });

    // Initialize ref when entering edit mode with actual data
    useEffect(() => {
        if (isMKTEditing && mktCostProjectsData.length > 0 && MKTInitialParamsRef.current.tableData.length === 0) {
            MKTInitialParamsRef.current = {
                tableData: JSON.parse(JSON.stringify(mktCostProjectsData)),
                pendingDeletes: [],
            };
        }
    }, [isMKTEditing, mktCostProjectsData]);

    // Start auto-saving MKT when in edit mode
    useAutoSaveParams({
        pageKey: MKTPageKey,
        initialParams: MKTInitialParamsRef.current,
        saveParamsCur: MKTCurrentSaveParams,
        isBegin: isMKTEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleMKTEdit = useCallback(() => {
        if (showMKTRestorePrompt(MKTPageKey)) {
            setMKTAutoSaveVisible(true);
        } else {
            MKTRef.current?.handleEdit();
        }
    }, [showMKTRestorePrompt, MKTPageKey, MKTRef]);

    // Handler: Discard auto-save and enter edit mode
    const handleMKTAutoSaveCancel = useCallback(() => {
        setMKTAutoSaveVisible(false);
        deleteMKTSaveParamsByPageKey(MKTPageKey);
        setMKTPendingDeletes([]); // Clear pending deletes when discarding auto-save
        MKTRef.current?.handleEdit();
    }, [deleteMKTSaveParamsByPageKey, MKTPageKey, MKTRef]);

    // Handler: Restore from auto-save
    const handleMKTAutoSaveConfirm = useCallback(() => {
        const saveValue = getMKTSaveParamsValue(MKTPageKey) || {};

        console.log('🔄 [MKT AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setMKTAutoSaveVisible(false);

        // Restore pending deletes if they exist
        if (saveValue.pendingDeletes && Array.isArray(saveValue.pendingDeletes)) {
            console.log('🗑️ [MKT AUTO-SAVE RESTORE] Restoring pending deletes:', saveValue.pendingDeletes.length, 'items');
            setMKTPendingDeletes(saveValue.pendingDeletes);
        }

        // 🔥 CRITICAL: Update mktCostProjectsData with saved data
        if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0) {
            setMktCostProjectsData(saveValue.tableData);
            // 🔥 CRITICAL: Do NOT update originalMktCostData here!
            // originalMktCostData should remain the API's original data for comparison
        }

        // 🔥 CRITICAL: Set manual update flag to prevent polling from overwriting restored data
        isMKTManualUpdateRef.current = true;

        // Update edit data state for auto-save tracking
        if (saveValue.tableData && Array.isArray(saveValue.tableData)) {
            setMKTEditData(saveValue.tableData);
        }

        // Enter edit mode after state updates
        setTimeout(() => {
            MKTRef.current?.handleEdit();
            // Expand categories after restoring data from auto-save
            if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0 && expandAllCategoriesWithChildren) {
                expandAllCategoriesWithChildren(saveValue.tableData);
            }
        }, 100);
    }, [getMKTSaveParamsValue, MKTPageKey, MKTRef, setMktCostProjectsData, expandAllCategoriesWithChildren]);

    // Clear auto-save after successful save
    const clearMKTAutoSave = useCallback(() => {
        deleteMKTSaveParamsByPageKey(MKTPageKey);
        setMKTPendingDeletes([]); // Clear pending deletes on successful save
    }, [deleteMKTSaveParamsByPageKey, MKTPageKey]);

    // Expose manual update ref for external operations (like drag & drop)
    const setManualUpdate = useCallback(() => {
        isMKTManualUpdateRef.current = true;
    }, []);

    // Check if there are unsaved changes (for RoutePrompt)
    const hasMKTUnsavedChanges = useMemo(() => {
        if (!isMKTEditing) return false;
        
        const initialData = MKTInitialParamsRef.current?.tableData;
        if (!initialData || initialData.length === 0) return false;
        
        const currentData = MKTEditData.length > 0 ? MKTEditData : mktCostProjectsData;
        
        // Compare using JSON stringify
        const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
        
        return hasChanges;
    }, [isMKTEditing, MKTEditData, mktCostProjectsData]);

    return {
        // State
        MKTAutoSaveVisible,
        MKTEditData,
        MKTPendingDeletes,
        MKTPageKey,
        hasMKTUnsavedChanges,

        // State setters (for external updates)
        setMKTEditData,
        setMKTPendingDeletes,
        setManualUpdate,

        // Handlers
        handleMKTEdit,
        handleMKTAutoSaveCancel,
        handleMKTAutoSaveConfirm,
        clearMKTAutoSave,
    };
};
